// Meta Pixel helper. Every event gets a unique event_id attached via fbq's
// eventID option, so browser events can be de-duplicated against the
// server-side Conversions API send (which reuses the same event_id).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export function genEventId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) { /* fall through */ }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : undefined;
  } catch (e) { return undefined; }
}

// Internal-device flag: set once a browser logs into /admin. GA4 hits from
// that device carry traffic_type=internal (matched by the GA4 internal-traffic
// data filter); Meta pixel + first-party counter skip it entirely.
export function isInternalDevice() {
  try { return window.__saebitInternal === true || localStorage.getItem("saebit_internal") === "1"; }
  catch (e) { return false; }
}

export function markInternalDevice() {
  try {
    localStorage.setItem("saebit_internal", "1");
    window.__saebitInternal = true;
  } catch (e) { /* private mode */ }
}

// Mirror a browser event to the server-side Conversions API using the SAME
// event_id for deduplication. Best-effort, non-blocking; PII is hashed on the
// backend (never in frontend code).
export function sendServerEvent(eventName, eventId, userData = {}) {
  if (!BACKEND_URL || !eventId) return;
  const body = {
    event_name: eventName,
    event_id: eventId,
    event_source_url: typeof window !== "undefined" ? window.location.href : undefined,
    fbp: readCookie("_fbp"),
    fbc: readCookie("_fbc"),
    ...userData,
  };
  try {
    fetch(`${BACKEND_URL}/api/track/capi`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch (e) { /* never block the funnel */ }
}

// Fire a browser pixel event (with a unique event_id) and mirror it to the
// Conversions API server-side. Returns the event_id.
const CAPI_MIRRORED_EVENTS = new Set(["Lead", "Schedule", "CompleteRegistration", "SubmitApplication"]);

export function trackPixel(event, params = {}, userData = {}) {
  if (isInternalDevice()) return null;
  const eventID = genEventId();
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("track", event, params, { eventID });
  }
  if (CAPI_MIRRORED_EVENTS.has(event)) {
    sendServerEvent(event, eventID, userData);
  }
  return eventID;
}

export const trackPageView = (params = {}) => trackPixel("PageView", params);
export const trackLead = (params = {}, userData = {}) => trackPixel("Lead", params, userData);
export const trackSchedule = (params = {}, userData = {}) => trackPixel("Schedule", params, userData);

// Fire a GA4 (gtag) custom event. Never blocks the funnel.
function gtagEvent(name, params = {}) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, params);
    }
  } catch (e) { /* never block the funnel */ }
}

// Funnel events. quiz start (Lead) → quiz complete (CompleteRegistration) →
// founding-list signup (SubmitApplication). Mirrored server-side (Meta CAPI)
// for dedup, and also sent to GA4 as custom events for the same funnel view.
export const trackQuizStart = () => {
  const id = trackPixel("Lead", { content_name: "quiz_start" });
  gtagEvent("quiz_start");
  return id;
};
export const trackQuizComplete = () => {
  const id = trackPixel("CompleteRegistration", { content_name: "quiz_complete" });
  gtagEvent("quiz_complete");
  return id;
};
export const trackFoundingSignup = (userData = {}) => {
  const id = trackPixel("SubmitApplication", { content_name: "founding_signup" }, userData);
  gtagEvent("founding_signup");
  gtagEvent("generate_lead", { currency: "MYR", value: 240 });
  return id;
};

// First-party visit counter (independent of Meta, no ad-blocker gaps).
export function trackVisit(path) {
  if (!BACKEND_URL || isInternalDevice()) return;
  const p = path || (typeof window !== "undefined" ? window.location.pathname : "/");
  try {
    fetch(`${BACKEND_URL}/api/track/pageview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: p }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) { /* never block */ }
}
